import {useState} from 'react';
import {sanitize} from '../../helpers';
import RequestError from '../../api/RequestError';
import {handleApiValidationErrors} from '../../helpers/error';
import {Form, Formik} from 'formik';
import {ButtonWithLoader} from '../common/Button';
import {updateCurrentUser} from '../../api/users';
import {
  currentUserProps, setUserDetails
} from '../../store/currentUser/currentUser.slice';
import EnsurePermission from '../common/EnsurePermission';
import permissionsInfo from '../../constants/permissionsInfo';
import {useDispatch} from 'react-redux';
import {editUserValidationSchema} from '../../constants/validationSchemes';
import {EditUserProps} from '../../constants/types';
import {useAppSelector} from '../../store/hooks';
import {RootState} from '../../store/store';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import {TextField} from '../common/formik/fields';
import {useTranslation} from 'react-i18next';
import {FormGroup} from '@mui/material';
import {useSnackbarHook} from '../../hooks/useSnackbar';

enum Field {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName'
}

export const EditUserProfile = () => {
  const {t} = useTranslation('user');
  const dispatch = useDispatch();
  const [apiValidationError, setApiValidationError] =
    useState<Nullable<Dictionary<string[]>>>(null);
  const currentUser: currentUserProps = useAppSelector(
      (state: RootState) => state.user
  );
  const {firstName, lastName} = currentUser || {};
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const clearMessages = () => {
    setApiValidationError(null);
  };

  const initialValues = {
    firstName: firstName || '',
    lastName: lastName || ''
  };

  const onSubmit = async (values: EditUserProps) => {
    const sanitizeValues = sanitize(values);

    try {
      await updateCurrentUser(sanitizeValues);
      dispatch(setUserDetails({...currentUser, ...sanitizeValues}));
      addSuccessToast(t('toasts.updateUserMessage'));
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'user');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  return (
    <div className="tab-content">
      <Formik
        initialValues={initialValues}
        validationSchema={editUserValidationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({dirty, isSubmitting, isValid, submitForm}) => {
          useOnUnsavedChanges(dirty);

          return (
            <>
              <Form className="form">
                <FormGroup>
                  <TextField
                    name={Field.FIRST_NAME}
                    label={t('form.fields.firstName.label')}
                    disabled={isSubmitting}
                    required
                    onFocus={() => setApiValidationError(null)}
                    onBlur={() => clearMessages()}
                    apiValidationError={apiValidationError}
                  />
                </FormGroup>

                <FormGroup>
                  <TextField
                    name={Field.LAST_NAME}
                    label={t('form.fields.lastName.label')}
                    disabled={isSubmitting}
                    required
                    onFocus={() => setApiValidationError(null)}
                    onBlur={() => clearMessages()}
                    apiValidationError={apiValidationError}
                  />
                </FormGroup>
              </Form>

              <div className="buttons">
                <EnsurePermission
                  elementPermissions={permissionsInfo.users.profile.edit}
                >
                  <ButtonWithLoader
                    disabled={!(isValid && dirty)}
                    isSubmitting={isSubmitting}
                    label={t('profile.buttons.updateAccount')}
                    onClick={submitForm}
                  />
                </EnsurePermission>
              </div>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default EditUserProfile;
